import { StrictMode, useEffect, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { CI_THEME } from '@pst/shared';
import '@mantine/core/styles.css';
import './app.css';
import { App } from './App';
import { themeApi } from './api';
import { UndoProvider } from './ui/undo';
import { makeTheme } from './theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means "log in again", not "try harder".
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * The office's colours from Einstellungen › Darstellung. Until they have
 * loaded - and if they cannot be - the corporate design applies, which is
 * also the default, so nothing flickers in the usual case.
 */
function ThemeRoot({ children }: { children: ReactNode }): JSX.Element {
  const colors = useQuery({ queryKey: ['theme'], queryFn: themeApi.get, staleTime: Infinity });
  const { theme, resolver } = useMemo(() => makeTheme(colors.data ?? CI_THEME), [colors.data]);
  const primary = colors.data?.primary ?? CI_THEME.primary;
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', primary);
  }, [primary]);
  return <MantineProvider theme={theme} cssVariablesResolver={resolver} defaultColorScheme="auto">{children}</MantineProvider>;
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element missing');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeRoot>
        <UndoProvider>
          <App />
        </UndoProvider>
      </ThemeRoot>
    </QueryClientProvider>
  </StrictMode>,
);
